import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  GridItem,
  Text,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react';
import { documentAction, translationAction } from 'actions';
import FilePreviewDialog, { PreviewFileState } from 'containers/Summary/FilePreviewDialog';
import { HTTP_CODE_RESPONSE, TEMPORARY } from 'helpers/constant';
import { addTokenURL, getFileExtension } from 'helpers/file';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UploadService } from 'services/upload/upload.service';
import { FileDocument } from 'types/reducers/document';
import { Task } from 'types/reducers/task';
import { getStorageReadBlobToken } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

import LanguagesMode from './LanguagesMode';
import SelectFiles from './SelectFiles';
import StartTask from './StartTask';
import TranslatedFiles from './TranslatedFiles';
import TransLationJobs from './TranslationJobs';

const Translator = () => {
  const dispatch = useDispatch();
  const { tasks, current } = useSelector((state) => state.translation);
  const { t } = useTranslation();

  const [_, setIsLoading] = useState<boolean>(false);
  const [isShowingPreviewFile, setIsShowingPreviewFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFileState>({});
  const [isToggleSidebar, setIsToggleSidebar] = useState(true);

  // Mobile/tablet reflow: Jobs panel lives in a left Drawer (mirrors Ask/Summary).
  // Desktop (xl+) keeps the multi-pane grid. (design-guidelines.md §5, QA mobile clip fix)
  const { isOpen, onOpen: onOpenSidebarMobile, onClose: onCloseSidebarMobile } = useDisclosure();
  const [isDesktop] = useMediaQuery('(min-width: 80em)');

  const newTask = useMemo(
    () => ({
      id: uuidv4(),
      type: TEMPORARY,
      name: t('common.addNew'),
      last_updated: new Date().toISOString(),
    }),
    [t]
  );

  const isTranslationJobSelecting = Boolean(current && current.task_id);

  const loadData = () => {
    setIsLoading(true);
    dispatch(translationAction.resetData());
    dispatch(translationAction.getTranslationTasks());
    setIsLoading(false);
  };

  const onChangeTask = (item: Task) => {
    dispatch(
      translationAction.updateCurrentTranslationTask({
        task_id: item?.id,
        type: item?.type,
      })
    );
  };

  const addNewTask = () => {
    const temporaryTask = tasks.data.find((task) => task.type === TEMPORARY);
    if (temporaryTask) {
      dispatch(
        translationAction.updateCurrentTranslationTask({
          task_id: temporaryTask.id,
          type: temporaryTask?.type,
        })
      );
      return;
    }
    dispatch(translationAction.addTranslationTask(newTask));
    dispatch(translationAction.clearTemporaryTask());
    dispatch(translationAction.updateTemporaryTaskId(newTask.id));
    onChangeTask(newTask);
  };

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = Number(process.env.REACT_APP_TIMEOUT_REFRESH_SAS_TOKEN ?? 0) * 1000;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (file: FileDocument) => {
    if (!file.url) {
      return;
    }
    readFile(file);
  };

  const readFile = (file: FileDocument) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(file);
  };

  const fetchData = (file: FileDocument) => {
    const fileUrl = addTokenURL(file?.url, getStorageReadBlobToken());
    setPreviewFile({
      url: fileUrl,
      name: file?.name,
      type: getFileExtension(file?.url),
    });

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(file);
        } else {
          setIsShowingPreviewFile(true);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  const onClose = () => {
    setIsShowingPreviewFile(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Open Jobs drawer from the NavBar hamburger (CustomEvent shared with Ask/Summary)
  useEffect(() => {
    const handleOpenSidebarMobile = () => onOpenSidebarMobile();
    window.addEventListener('open-sidebar-mobile', handleOpenSidebarMobile);
    return () => window.removeEventListener('open-sidebar-mobile', handleOpenSidebarMobile);
  }, [onOpenSidebarMobile]);

  // Close the drawer once we reach desktop so the grid takes over cleanly
  useEffect(() => {
    if (isDesktop) onCloseSidebarMobile();
  }, [isDesktop]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selecting a job from the drawer should reveal the panes on mobile
  const onChangeTaskMobile = (item: Task) => {
    onChangeTask(item);
    onCloseSidebarMobile();
  };

  return (
    /* DS translator shell — canvas bg; multi-pane grid on desktop, stacked + Jobs Drawer on
       mobile/tablet so nothing clips/overlaps (design-guidelines.md §5, SC-03, QA mobile fix) */
    <Box h='100%' minH={0} bg='bg.canvas'>
      <FilePreviewDialog isOpen={isShowingPreviewFile} file={previewFile} onClose={onClose} />

      {/* Desktop (xl+): original 12-col multi-pane grid */}
      <Grid
        gap={4}
        templateColumns='repeat(12,1fr)'
        display={{ base: 'none', xl: 'grid' }}
        h='100%'
      >
        <GridItem w={'100%'} minW={0} colSpan={isToggleSidebar ? 3 : 1}>
          <TransLationJobs
            onAddNew={addNewTask}
            onChange={onChangeTask}
            isToggleSidebar={isToggleSidebar}
            setIsToggleSidebar={setIsToggleSidebar}
          />
        </GridItem>
        <GridItem
          w={'100%'}
          minW={0}
          colSpan={3}
          hidden={current && current.task_id ? false : true}
        >
          <SelectFiles onOpenFile={onOpenFile} />
        </GridItem>
        <GridItem
          w={'100%'}
          minW={0}
          colSpan={isToggleSidebar ? 3 : 4}
          hidden={!isTranslationJobSelecting}
        >
          <LanguagesMode />
        </GridItem>
        <GridItem
          w={'100%'}
          minW={0}
          colSpan={isToggleSidebar ? 3 : 4}
          hidden={!isTranslationJobSelecting}
        >
          <TranslatedFiles />
        </GridItem>
        <GridItem
          transition='width .05s ease-in-out'
          colSpan={isToggleSidebar ? 9 : 11}
          hidden={isTranslationJobSelecting}
          placeItems={'center'}
        >
          <Flex align={'center'} justify={'center'} w={'100%'} h={'100%'}>
            <StartTask onAddNew={addNewTask} />
          </Flex>
        </GridItem>
      </Grid>

      {/* Mobile/tablet (< xl): single-column full-width stack; Jobs in a left Drawer */}
      <Box display={{ base: 'block', xl: 'none' }} overflowY='auto' h='100%'>
        {isTranslationJobSelecting ? (
          <Flex direction='column' gap={4} h='100%'>
            <Box hidden={current && current.task_id ? false : true}>
              <SelectFiles onOpenFile={onOpenFile} />
            </Box>
            <Box>
              <LanguagesMode />
            </Box>
            <Box>
              <TranslatedFiles />
            </Box>
          </Flex>
        ) : (
          <Flex align='center' justify='center' w='100%' h='100%'>
            <StartTask onAddNew={addNewTask} />
          </Flex>
        )}

        <Drawer isOpen={isOpen} onClose={onCloseSidebarMobile} placement='left'>
          <DrawerOverlay bg='blackAlpha.500' backdropFilter='blur(3px)' />
          <DrawerContent bg='bg.sidebar'>
            <DrawerCloseButton color='text.muted' />
            <DrawerHeader
              color='text.default'
              fontFamily='heading'
              fontSize='lg'
              fontWeight={600}
              borderColor='border.default'
              borderBottomWidth='1px'
            >
              <Text>{t('translator.translationJobs')}</Text>
            </DrawerHeader>
            <DrawerBody px={2} py={3}>
              {/* Force expanded list inside drawer; collapse toggle is desktop-only */}
              <TransLationJobs
                onAddNew={addNewTask}
                onChange={onChangeTaskMobile}
                isToggleSidebar={true}
                setIsToggleSidebar={setIsToggleSidebar}
                isInDrawer
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Box>
    </Box>
  );
};

export default Translator;
